import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Phone, MapPin, Clock, Mail, Moon, Sun } from 'lucide-react';
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true';
    setDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', String(newDarkMode));
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };
  const navItems = [{
    href: '/',
    label: 'Home'
  }, {
    href: '/servizi',
    label: 'Servizi'
  }, {
    href: '/chi-siamo',
    label: 'Chi Siamo'
  }, {
    href: '/contatti',
    label: 'Contatti'
  }, {
    href: '/articoli',
    label: 'Articoli'
  }, {
    href: '/tariffe',
    label: 'Tariffe'
  }, {
    href: '/consenso-informato',
    label: 'Consenso'
  }, {
    href: '/prenotazioni',
    label: 'Prenota'
  }];
  const isActive = (href: string) => location.pathname === href;
  return <>
      {/* Top Bar con informazioni di contatto */}
      <div className="bg-primary text-white py-2 hidden md:block">
        <div className="container-custom">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4" />
                <a href="tel:+393475391433" className="hover:underline">+39 347 5391433</a>
              </div>
              <a href="mailto:info@riabilitazionemotoriapapa.it" className="flex items-center space-x-2 hover:text-accent smooth-transition">
                <Mail className="h-4 w-4" />
                <span>info@riabilitazionemotoriapapa.it</span>
              </a>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4" />
                <span>Lun-Ven: 8:00-19:00 | Sab: 8:00-13:00</span>
              </div>
              <a 
                href="https://wa.me/393475391433" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 px-3 py-1 rounded-full smooth-transition"
                title="Scrivici su WhatsApp"
              >
                <svg fill="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span className="text-sm font-medium">WhatsApp</span>
              </a>
              <div className="flex items-center space-x-2 border-l border-white/20 pl-4">
                <a 
                  href="https://g.co/kgs/VAvHRR" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-6 h-6 hover:text-accent smooth-transition"
                  title="Google My Business"
                >
                  <svg fill="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
                    <path d="M12.017 14.634c-.47 0-.799-.34-.799-.799s.329-.799.799-.799.799.34.799.799-.329.799-.799.799zm3.648-3.648c0-2.012-1.636-3.648-3.648-3.648S8.369 8.974 8.369 10.986s1.636 3.648 3.648 3.648 3.648-1.636 3.648-3.648zm2.183 0c0 3.218-2.613 5.831-5.831 5.831S6.186 14.204 6.186 10.986s2.613-5.831 5.831-5.831 5.831 2.613 5.831 5.831z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.instagram.com/riabilitazionemotoriapapa" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-6 h-6 hover:text-accent smooth-transition"
                  title="Instagram"
                >
                  <svg fill="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.linkedin.com/in/peppino-salvatore-papa-021b37231" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-6 h-6 hover:text-accent smooth-transition"
                  title="LinkedIn"
                >
                  <svg fill="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation principale */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="container-custom">
          <div className="flex justify-between items-center py-3">
            {/* Logo - Posizionamento ottimizzato */}
            <Link to="/" className="logo-container flex items-center">
              <img 
                src="/images/logo-rmp-1024.png" 
                alt="Centro di Riabilitazione Papa - Riabilitazione Motoria" 
                className="logo-main"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              {navItems.map(item => <Link key={item.href} to={item.href} className={`text-sm font-medium smooth-transition hover:text-primary ${isActive(item.href) ? 'text-primary border-b-2 border-primary pb-1' : 'text-foreground'}`}>
                  {item.label}
                </Link>)}
              <Button 
                variant="outline" 
                size="icon"
                onClick={toggleDarkMode}
                className="ml-2"
                title={darkMode ? "Modalità Chiara" : "Modalità Notturna"}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button className="medical-gradient text-white hover:opacity-90" onClick={() => window.location.href = '/prenotazioni'}>
                Prenota Visita
              </Button>
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={toggleDarkMode}
                title={darkMode ? "Modalità Chiara" : "Modalità Notturna"}
              >
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon">
                    <Menu className="h-6 w-6" />
                  </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col space-y-6 mt-6">
                  <div className="flex items-center space-x-3 pb-4 border-b">
                    <img 
                      src="/images/logo-rmp-1024.png" 
                      alt="Centro di Riabilitazione Papa - Logo" 
                      className="logo-mobile"
                    />
                    <div>
                      <h2 className="font-bold text-lg">Centro Papa</h2>
                      <p className="text-sm text-muted-foreground">Riabilitazione Motoria</p>
                    </div>
                  </div>
                  
                  {navItems.map(item => <Link key={item.href} to={item.href} onClick={() => setIsOpen(false)} className={`text-lg font-medium smooth-transition hover:text-primary ${isActive(item.href) ? 'text-primary' : 'text-foreground'}`}>
                      {item.label}
                    </Link>)}
                  
                  <div className="pt-4 space-y-4">
                    <Button 
                      className="w-full medical-gradient text-white" 
                      onClick={() => {
                        window.open('https://www.sumupbookings.com/dott-papa-peppino-salvatore', '_blank');
                        setIsOpen(false);
                      }}
                    >
                      Prenota Visita
                    </Button>
                    
                    <div className="space-y-3 text-sm">
                      <a href="tel:+393475391433" className="flex items-center space-x-2 hover:text-primary smooth-transition">
                        <Phone className="h-4 w-4 text-primary" />
                        <span>+39 347 5391433</span>
                      </a>
                      <a 
                        href="https://wa.me/393475391433" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 smooth-transition"
                      >
                        <svg fill="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <span className="font-medium">Scrivici su WhatsApp</span>
                      </a>
                      <a href="mailto:info@riabilitazionemotoriapapa.it" className="flex items-center space-x-2 hover:text-accent smooth-transition">
                        <Mail className="h-4 w-4 text-primary" />
                        <span>info@riabilitazionemotoriapapa.it</span>
                      </a>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-primary" />
                        <span>Lun-Ven: 8:00-19:00</span>
                      </div>
                    </div>
                  </div>
                </div>
              </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>
    </>;
};
export default Navigation;